import { ApiError, type DownloadedFile } from '../client';
import {
  QrBatchCreatedSchema,
  QrBatchListSchema,
  QrBatchStatsSchema,
  type QrBatchCreated,
  type QrBatchList,
  type QrBatchStats,
} from '../schemas/qrBatches';

// The in-memory generator behind VITE_USE_MOCK_API. It has to MOVE — a request queues a batch and
// the batch finishes on its own — or neither the polling nor the download can be walked by hand.

// How long a mock batch takes to «generate». Shorter than the screen's 60 s poll, so the next poll
// after a request already finds it done.
export const MOCK_QR_GENERATION_MS = 30_000;

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const CODE_LENGTH = 7;
const TOTAL_SPACE = ALPHABET.length ** CODE_LENGTH;
const MAX_AMOUNT = 100_000;

interface WireBatch {
  id: string;
  lock_model: string;
  qr_amount: number;
  status: 'PENDING' | 'DONE' | 'FAILED';
  created_at: string;
  finished_at: string | null;
  has_file: boolean;
  in_use: number;
  // Where this batch's codes start in the mock's code sequence, so a file is reproducible.
  first_code: number;
}

// Fixed instants, never `new Date()` in a seed: a fixture that moves is a test that fails at
// midnight. The CBX10 row is the 2022 legacy kind: 5-character codes, no file kept.
const SEED: readonly WireBatch[] = [
  {
    id: 'qrb-2026-09-02',
    lock_model: 'LOCK1',
    qr_amount: 20_000,
    status: 'DONE',
    created_at: '2026-09-02T08:12:00Z',
    finished_at: '2026-09-02T08:19:00Z',
    has_file: true,
    in_use: 6_430,
    first_code: 10_000,
  },
  {
    id: 'qrb-2026-07-10',
    lock_model: 'LOCK1',
    qr_amount: 5_000,
    status: 'FAILED',
    created_at: '2026-07-10T11:40:00Z',
    finished_at: '2026-07-10T11:41:00Z',
    has_file: false,
    in_use: 0,
    first_code: 0,
  },
  {
    id: 'qrb-2026-06-03',
    lock_model: 'LOCK1',
    qr_amount: 10_000,
    status: 'DONE',
    created_at: '2026-06-03T09:05:00Z',
    finished_at: '2026-06-03T09:09:00Z',
    has_file: true,
    in_use: 9_812,
    first_code: 0,
  },
  {
    id: 'qrb-2022-03-14',
    lock_model: 'CBX10',
    qr_amount: 30_000,
    status: 'DONE',
    created_at: '2022-03-14T10:00:00Z',
    finished_at: '2022-03-14T10:30:00Z',
    has_file: false,
    in_use: 24_950,
    first_code: 0,
  },
];

let batches: WireBatch[] = [];
let sequence = 0;

// Exported for tests: module state outlives an `it`.
export function resetQrBatchesMock(): void {
  batches = SEED.map((batch) => ({ ...batch }));
  sequence = 0;
}

resetQrBatchesMock();

const latency = () => new Promise((resolve) => setTimeout(resolve, 100));

function codeAt(index: number): string {
  let rest = index;
  let code = '';
  for (let position = 0; position < CODE_LENGTH; position += 1) {
    code = `${ALPHABET[rest % ALPHABET.length] ?? 'A'}${code}`;
    rest = Math.floor(rest / ALPHABET.length);
  }
  return code;
}

const lock1Done = () => batches.filter((batch) => batch.lock_model === 'LOCK1' && batch.status === 'DONE');

// A pending batch finishes once its generation time has passed, read lazily on the next request.
function settle(): void {
  const now = Date.now();
  for (const batch of batches) {
    if (batch.status === 'PENDING' && now - Date.parse(batch.created_at) >= MOCK_QR_GENERATION_MS) {
      batch.status = 'DONE';
      batch.finished_at = new Date(now).toISOString();
      batch.has_file = true;
      batch.first_code = lock1Done().reduce((sum, done) => sum + done.qr_amount, 0) + 100_000;
    }
  }
}

const round = (value: number) => Math.round(value * 100) / 100;

const notFound = () =>
  new ApiError(
    404,
    'qr_batch_file_not_found',
    { code: 'qr_batch_file_not_found', message: 'QR batch file not found', detail: '' },
    'QR batch file not found',
  );

export async function fetchQrBatchesMock(): Promise<QrBatchList> {
  await latency();
  settle();
  const items = [...batches]
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .map((batch) => ({
      ...batch,
      in_use_percentage: batch.qr_amount === 0 ? 0 : round((batch.in_use / batch.qr_amount) * 100),
    }));
  // Through the same parse as the real read, so the mock cannot drift from the wire shape.
  return QrBatchListSchema.parse({
    items,
    has_pending: items.some((batch) => batch.status === 'PENDING'),
  });
}

export async function requestQrBatchMock(qrAmount: number): Promise<QrBatchCreated> {
  await latency();
  settle();
  if (!Number.isInteger(qrAmount) || qrAmount < 1 || qrAmount > MAX_AMOUNT) {
    throw new ApiError(
      400,
      'invalid_qr_amount',
      { code: 'invalid_qr_amount', message: 'qr_amount must be between 1 and 100000', detail: '' },
      'Invalid QR amount',
    );
  }
  if (batches.some((batch) => batch.status === 'PENDING')) {
    throw new ApiError(
      409,
      'pending_qr_batch_exists',
      { code: 'pending_qr_batch_exists', message: 'A QR batch is already pending', detail: '' },
      'A QR batch is already pending',
    );
  }
  sequence += 1;
  const batch: WireBatch = {
    id: `qrb-mock-${String(sequence)}`,
    lock_model: 'LOCK1',
    qr_amount: qrAmount,
    status: 'PENDING',
    created_at: new Date(Date.now()).toISOString(),
    finished_at: null,
    has_file: false,
    in_use: 0,
    first_code: 0,
  };
  batches.push(batch);
  return QrBatchCreatedSchema.parse(batch);
}

export async function fetchQrBatchFileMock(id: string): Promise<DownloadedFile> {
  await latency();
  settle();
  const batch = batches.find((candidate) => candidate.id === id);
  if (!batch?.has_file) throw notFound();
  const lines = Array.from({ length: batch.qr_amount }, (_, index) => codeAt(batch.first_code + index));
  return {
    blob: new Blob([`${lines.join('\n')}\n`], { type: 'text/csv' }),
    filename: `${batch.id}.csv`,
  };
}

export async function fetchQrBatchStatsMock(): Promise<QrBatchStats> {
  await latency();
  settle();
  const done = lock1Done();
  const generated = done.reduce((sum, batch) => sum + batch.qr_amount, 0);
  const inUse = done.reduce((sum, batch) => sum + batch.in_use, 0);
  return QrBatchStatsSchema.parse({
    alphabet: ALPHABET,
    code_length: CODE_LENGTH,
    total_space: TOTAL_SPACE,
    generated,
    in_use: inUse,
    unused: generated - inUse,
    space_used_percentage: round((generated / TOTAL_SPACE) * 100),
    in_use_percentage: generated === 0 ? 0 : round((inUse / generated) * 100),
    remaining: TOTAL_SPACE - generated,
  });
}
